import { ariaAttributes } from '@lib/utils/aria'
import type { WithTestId } from '@lib/utils/testid'
import classNames from 'classnames'
import type { AnchorHTMLAttributes } from 'react'
import { forwardRef } from 'react'

import styles from './button.module.scss'
import { ButtonContent } from './content'
import { PRESETS } from './presets'
import type { CommonButtonProps } from './props'

/**
 *  Renders a link with the same design as a regular button.
 * To use with Next.js Link, set asChild={true} and wrap with <Link>:
 * <Link href="/path"><LinkButton asChild>Click me</LinkButton></Link>
 */
export const LinkButton = forwardRef<
    HTMLAnchorElement | HTMLSpanElement,
    LinkButtonProps
>(
    (
        {
            'data-testid': testId,
            analyticsProperties,
            asChild = false,
            children,
            className,
            containerElementId,
            download,
            href,
            loading,
            media,
            onClick,
            placement,
            preset = 'primary',
            referrerPolicy,
            rel,
            size = 'medium',
            style,
            target,
            title,
            disabled,
            ...restOfProps
        },
        ref,
    ) => {
        const presetStyles =
            PRESETS[preset as keyof typeof PRESETS] ?? PRESETS.primary

        const commonProps = {
            ...ariaAttributes(restOfProps),
            className: classNames(
                styles.button,
                presetStyles.button,
                presetStyles[size],
                disabled && styles.disabled,
                className,
            ),
            'data-testid': testId,
            onClick: (e: React.MouseEvent<HTMLElement>) => {
                onClick?.(e as React.MouseEvent<HTMLAnchorElement>)
            },
            style,
            title,
        }

        if (asChild) {
            return (
                <span {...commonProps} ref={ref as React.Ref<HTMLSpanElement>}>
                    <ButtonContent>{children}</ButtonContent>
                </span>
            )
        }

        return (
            <a
                {...commonProps}
                download={download}
                media={media}
                href={href}
                ref={ref as React.Ref<HTMLAnchorElement>}
                referrerPolicy={referrerPolicy}
                rel={rel}
                target={target}
            >
                <ButtonContent>{children}</ButtonContent>
            </a>
        )
    },
)

export type LinkButtonProps = WithTestId<
    CommonButtonProps &
        Pick<
            AnchorHTMLAttributes<HTMLAnchorElement>,
            | 'href'
            | 'title'
            | 'children'
            | 'download'
            | 'media'
            | 'onClick'
            | 'referrerPolicy'
            | 'rel'
            | 'style'
            | 'target'
        > & {
            disabled?: boolean
            preset?: string
            /**
             * When true, renders as a <span> instead of <a>.
             * Use this when wrapping with Next.js Link to avoid nested anchors.
             */
            asChild?: boolean
        }
>
